/**
 * **带符号百分数文本** —— 端侧唯一实现（`DR-036` D4 · 铁律 31）
 *
 * ## 为什么从 `major-asset.ts` 搬到这里（批 99 B2）
 *
 * 这段逻辑原是 R 域（重大资产）的私有导出。投资域的浮盈率 / 总收益率 / 已实现收益率 /
 * 年化**四处**都要同一份格式，若照抄一份，就成了「同一个数字两种排版」——
 * 而百分数是本项目最容易被写成两套的东西（一处 `−24.88%`、一处 `-24.88%`）。
 *
 * ⇒ 搬到公共处，`major-asset.ts` 改为从这里**再导出**（既有调用点与测试一字不改）。
 *
 * ## 🔴 三条约定
 *
 * | 约定 | 理由 |
 * |---|---|
 * | 减号用 **U+2212**（`−`）不用 ASCII `-` | 铁律 31：财务文本里的负号要和正文破折号一致；`-` 在等宽字体下像连字符 |
 * | 正数**显式带** `+` | 收益的正负是语义的一部分，"24.88%" 看不出赚还是亏 |
 * | `0` **不带符号** | `+0.00%` / `−0.00%` 都是噪音，且会让人以为有微小盈亏 |
 *
 * ## 🔴 这里**不做除法**
 *
 * 百分比文本由内核 `rateE4ToPercentText()` 切出来（定点白名单），本函数只负责
 * 取绝对值 + 加符号。负值必须先取绝对值：内核该函数对负数是**向 0 截断**的，
 * 直接传 `-2488` 会切出 `-24.-88`（已在 `major-asset.spec.ts` 记过一次）。
 */

import { rateE4ToPercentText, type RateE4 } from '@jiaying/shared';

/** 万分之一整数 → 带符号百分比文本（`1737` → `"+17.37%"`；`-2488` → `"−24.88%"`） */
export function percentWithSign(rateE4: number): string {
  // 🔴 取绝对值再交给内核：`rateE4ToPercentText` 对负数是向 0 截断（`-2488` 会切出 `-24.-88`）
  const abs = rateE4 < 0 ? -rateE4 : rateE4;
  const body = rateE4ToPercentText(abs as RateE4);
  if (rateE4 === 0) return `${body}%`;
  // 铁律 31：减号用 U+2212（不是 ASCII `-`）
  return `${rateE4 < 0 ? '−' : '+'}${body}%`;
}

/** 万分之一整数 → **不带符号**的百分比文本（占比 / 使用率这类"中性比例"用） */
export function percentWithoutSign(rateE4: number): string {
  const abs = rateE4 < 0 ? -rateE4 : rateE4;
  return `${rateE4ToPercentText(abs as RateE4)}%`;
}
